import React, { useMemo, useState } from 'react';
import { AlertCircle, RefreshCw, Search, Server, X } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { useNode, useNodes, useNodeStats } from '../hooks/useNodes';
import { formatAbsoluteTime, formatBytes, formatNumber, formatRelativeTime, formatUptime } from '../utils/format';
import type { NodeDetail, NodeInfo } from '../types';

// The Nodes page reads the two node routes socket-cloud.openapi.yaml declares:
// GET /api/v1/nodes (NodeInfo list) and GET /api/v1/nodes/{nodeId} (NodeDetail).
// A field renders only when the contract declares it and the response carries
// it; the page has no update or remove action because neither has a contract
// (GH #1088).

// Keyed by the contract's NodeStatus enum, so a new status value fails tsc here.
const STATUS_LABEL: Record<NodeInfo['status'], string> = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  offline: 'Offline',
};

const STATUS_VALUES = Object.keys(STATUS_LABEL) as NodeInfo['status'][];

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent>
        <div className="text-sm text-gray-400">{label}</div>
        <div className="text-2xl font-semibold text-gray-100" data-testid={`node-stat-${label.toLowerCase()}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-gray-800 last:border-0 text-sm">
      <dt className="text-gray-400 shrink-0">{label}</dt>
      <dd className="text-gray-200 text-right break-all">{children}</dd>
    </div>
  );
}

function NodeDetailFields({ node }: { node: NodeDetail }) {
  const metrics = node.metrics;
  return (
    <dl>
      <DetailRow label="Node ID">
        <span className="font-mono">{node.nodeId}</span>
      </DetailRow>
      <DetailRow label="Address">
        <span className="font-mono">{node.address}</span>
      </DetailRow>
      <DetailRow label="Role">{node.role}</DetailRow>
      <DetailRow label="Status">
        <Badge variant="status" status={node.status} size="sm">
          {STATUS_LABEL[node.status]}
        </Badge>
      </DetailRow>
      <DetailRow label="Last seen">
        {formatAbsoluteTime(node.lastSeen)} ({formatRelativeTime(node.lastSeen)})
      </DetailRow>
      {node.version !== undefined && <DetailRow label="Version">{node.version}</DetailRow>}
      {node.uptime !== undefined && <DetailRow label="Uptime">{formatUptime(node.uptime)}</DetailRow>}
      {node.connections !== undefined && <DetailRow label="Connections">{node.connections}</DetailRow>}
      {node.consensusWeight !== undefined && <DetailRow label="Consensus weight">{node.consensusWeight}</DetailRow>}
      {node.capabilities !== undefined && node.capabilities.length > 0 && (
        <DetailRow label="Capabilities">{node.capabilities.join(', ')}</DetailRow>
      )}
      {node.publicKey !== undefined && (
        <DetailRow label="Public key">
          <span className="font-mono text-xs">{node.publicKey}</span>
        </DetailRow>
      )}
      {metrics?.messagesPerSecond !== undefined && (
        <DetailRow label="Messages per second">{formatNumber(metrics.messagesPerSecond)}</DetailRow>
      )}
      {metrics?.bytesIn !== undefined && <DetailRow label="Bytes in">{formatBytes(metrics.bytesIn)}</DetailRow>}
      {metrics?.bytesOut !== undefined && <DetailRow label="Bytes out">{formatBytes(metrics.bytesOut)}</DetailRow>}
      {metrics?.latency !== undefined && <DetailRow label="Average latency">{metrics.latency} ms</DetailRow>}
    </dl>
  );
}

function NodeDetailPanel({ nodeId, onClose }: { nodeId: string; onClose: () => void }) {
  const { data, isLoading, error } = useNode(nodeId);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <h2 className="font-medium text-gray-100">Node detail</h2>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close node detail">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && <div className="text-sm text-gray-400">Loading node...</div>}
        {error !== null && error !== undefined && (
          <div className="text-sm text-danger-400 flex items-center gap-2" role="alert">
            <AlertCircle className="w-4 h-4" />
            {errorText(error, 'Failed to load node')}
          </div>
        )}
        {!isLoading && !error && data && <NodeDetailFields node={data} />}
      </CardContent>
    </Card>
  );
}

export function NodesPage() {
  const { data: nodes = [], isLoading, error, refetch, isFetching } = useNodes();
  const stats = useNodeStats();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | NodeInfo['status']>('all');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const visibleNodes = useMemo(() => {
    const term = search.trim().toLowerCase();
    return nodes.filter((node) => {
      if (statusFilter !== 'all' && node.status !== statusFilter) {
        return false;
      }
      if (term === '') {
        return true;
      }
      return node.nodeId.toLowerCase().includes(term) || node.address.toLowerCase().includes(term);
    });
  }, [nodes, search, statusFilter]);

  const loaded = !isLoading && !error;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Server className="w-6 h-6 text-primary-400" />
          <div>
            <h1 className="text-xl font-semibold text-gray-100">Nodes</h1>
            <p className="text-sm text-gray-400">Nodes registered with socket-cloud-api, as GET /api/v1/nodes lists them</p>
          </div>
        </div>
        <Button size="sm" variant="secondary" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {isLoading && <div className="text-gray-400 text-sm">Loading nodes...</div>}
      {error !== null && error !== undefined && (
        <div className="text-danger-400 text-sm flex items-center gap-2" role="alert">
          <AlertCircle className="w-4 h-4" />
          {errorText(error, 'Failed to load nodes')}
        </div>
      )}

      {loaded && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Healthy" value={stats.healthy} />
          <StatCard label="Degraded" value={stats.degraded} />
          <StatCard label="Offline" value={stats.offline} />
        </div>
      )}

      {loaded && nodes.length === 0 && (
        <Card>
          <CardContent>
            <div className="text-gray-400 text-sm py-4">No nodes registered.</div>
          </CardContent>
        </Card>
      )}

      {loaded && nodes.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Card className="xl:col-span-2">
            <CardHeader>
              <div className="flex flex-wrap items-center gap-3">
                <label className="relative flex-1 min-w-48">
                  <span className="sr-only">Search nodes</span>
                  <Search className="w-4 h-4 text-gray-500 absolute left-2 top-1/2 -translate-y-1/2" />
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search by node ID or address"
                    className="w-full pl-8 pr-2 py-1.5 rounded bg-gray-900 border border-gray-700 text-sm text-gray-200"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-400">
                  Status
                  <select
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value as 'all' | NodeInfo['status'])}
                    className="py-1.5 px-2 rounded bg-gray-900 border border-gray-700 text-sm text-gray-200"
                  >
                    <option value="all">All</option>
                    {STATUS_VALUES.map((status) => (
                      <option key={status} value={status}>
                        {STATUS_LABEL[status]}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </CardHeader>
            <CardContent>
              {visibleNodes.length === 0 ? (
                <div className="text-gray-400 text-sm py-4">No nodes match the current filter.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-gray-800">
                        <th className="py-2 pr-4 font-medium">Node ID</th>
                        <th className="py-2 pr-4 font-medium">Address</th>
                        <th className="py-2 pr-4 font-medium">Role</th>
                        <th className="py-2 pr-4 font-medium">Status</th>
                        <th className="py-2 font-medium">Last seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleNodes.map((node) => (
                        <tr
                          key={node.nodeId}
                          className={`border-b border-gray-800 last:border-0 ${
                            node.nodeId === selectedNodeId ? 'bg-secondary-800/40' : ''
                          }`}
                        >
                          <td className="py-2 pr-4">
                            <button
                              type="button"
                              className="font-mono text-primary-400 hover:underline text-left"
                              onClick={() => setSelectedNodeId(node.nodeId)}
                            >
                              {node.nodeId}
                            </button>
                          </td>
                          <td className="py-2 pr-4 font-mono text-gray-300">{node.address}</td>
                          <td className="py-2 pr-4 text-gray-300">{node.role}</td>
                          <td className="py-2 pr-4">
                            <Badge variant="status" status={node.status} size="sm">
                              {STATUS_LABEL[node.status]}
                            </Badge>
                          </td>
                          <td className="py-2 text-gray-400" title={formatAbsoluteTime(node.lastSeen)}>
                            {formatRelativeTime(node.lastSeen)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
          <div>
            {selectedNodeId ? (
              <NodeDetailPanel nodeId={selectedNodeId} onClose={() => setSelectedNodeId(null)} />
            ) : (
              <Card>
                <CardContent>
                  <div className="text-gray-400 text-sm py-4">Select a node ID to read its detail.</div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
